import { createContext } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import _ from 'lodash';
import * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';

export interface AccumulatedModalChanges {
  values: Map<string, unknown>;
  responseSources: Map<string, QuestionnaireApiModels.QuestionnaireResponse['source']>;
  aiRequestIds: Map<string, string | null>;
  autofillStatuses: Map<string, boolean>;
  explanationHtmls: Map<string, string | null>;
}

export type QuestionnaireFormContextType = {
  formContextId: string;
  formMethods: UseFormReturn<FieldValues>;
  getExplanationHtml: (fieldKey: string) => string | null;
  setExplanationHtml: (fieldKey: string, explanationHtml: string | null) => void;
  getResponseSource: (fieldKey: string) => QuestionnaireApiModels.QuestionnaireResponse['source'];
  setResponseSource: (fieldKey: string, source: QuestionnaireApiModels.QuestionnaireResponse['source']) => void;
  getBulkResponseSources: (args?: { rowFieldKey: string }) => Record<string, QuestionnaireApiModels.QuestionnaireResponse['source']>
  mergeResponseSources: (source: Record<string, QuestionnaireApiModels.QuestionnaireResponse['source']>) => void;
  getAiRequestId: (fieldKey: string) => string | null;
  setAiRequestId: (fieldKey: string, aiRequestId: string | null) => void;
  getAutofillStatus: (fieldKey: string) => boolean;
  setAutofillStatus: (fieldKey: string, status: boolean) => void;
  getBulkAutofillStatuses: (args?: { rowFieldKey: string }) => Record<string, boolean>;
  mergeAutofillStatuses: (statuses: Record<string, boolean>) => void;
  getBulkAiRequestIds: (args?: { rowFieldKey: string }) => Record<string, string | null>;
  mergeAiRequestIds: (source: Record<string, string | null>) => void;
  setValue: (arg1: {
    key: string;
    value: unknown;
    source?: QuestionnaireApiModels.QuestionnaireResponse['source'];
    aiRequestId?: string | null;
    explanationHtml?: string | null;
    hasAutofillRun?: boolean;
  }) => void;
  isModalContext: boolean;
  modalLevel: number;
  getAccumulatedModalChanges: () => AccumulatedModalChanges;
  applyNestedModalChanges: (changes: AccumulatedModalChanges) => void;
  setCanSaveValueToDB: (value: boolean) => void;
  getCanSaveValueToDB: () => boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const unimplementedFunction: any = (): void => {
  throw new Error('Function not implemented.');
};

const QuestionnaireFormContext = createContext<QuestionnaireFormContextType>({
  applyNestedModalChanges: unimplementedFunction,
  formContextId: 'default',
  formMethods: {} as any, // eslint-disable-line @typescript-eslint/no-explicit-any
  getAccumulatedModalChanges: unimplementedFunction,
  getAiRequestId: unimplementedFunction,
  getAutofillStatus: unimplementedFunction,
  getBulkAiRequestIds: unimplementedFunction,
  getBulkAutofillStatuses: unimplementedFunction,
  getBulkResponseSources: unimplementedFunction,
  getCanSaveValueToDB: unimplementedFunction,
  getExplanationHtml: unimplementedFunction,
  getResponseSource: unimplementedFunction,
  isModalContext: false,
  mergeAiRequestIds: unimplementedFunction,
  mergeAutofillStatuses: unimplementedFunction,
  mergeResponseSources: unimplementedFunction,
  modalLevel: 0,
  setAiRequestId: unimplementedFunction,
  setAutofillStatus: unimplementedFunction,
  setCanSaveValueToDB: unimplementedFunction,
  setExplanationHtml: unimplementedFunction,
  setResponseSource: unimplementedFunction,
  setValue: unimplementedFunction,
});

export default QuestionnaireFormContext;
